/* eslint-disable @typescript-eslint/no-explicit-any */
// The Finance nodal officer's admin rights over the recovery heads:
//   • change the recovery head configured for a group (on transfer / posting) —
//     every pending checklist of that group moves from the old employee to the new;
//   • transfer pending checklists between employees — one, several or all.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { SectionCard, WarningBanner, useConfirm } from "./components";
import {
  FinanceAssignment,
  FinancePendingTask,
  changeFinanceAssignee,
  getFinanceAssignments,
  getFinancePendingTasks,
  transferFinanceTasks,
} from "../services/eSeparation.service";

const SCOPE_LABEL: Record<string, string> = { ALL: "All wings", OM: "O&M wing", PROJ: "Project wing" };

const FinanceNodalAdmin: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { confirm, confirmDialog } = useConfirm();
  const [assignments, setAssignments] = useState<FinanceAssignment[]>([]);
  const [pending, setPending] = useState<FinancePendingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newHead, setNewHead] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filterEmp, setFilterEmp] = useState("");
  const [transferTo, setTransferTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [a, p] = await Promise.all([getFinanceAssignments(), getFinancePendingTasks()]);
    setAssignments(a.data || []);
    setPending(p.data || []);
    setSelected(new Set());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (filterEmp ? pending.filter((t) => t.assignee_emp_id === filterEmp) : pending),
    [pending, filterEmp]
  );
  const assigneeOptions = useMemo(() => {
    const m = new Map<string, string>();
    pending.forEach((t) => m.set(t.assignee_emp_id, t.assignee_name || t.assignee_emp_id));
    return Array.from(m.entries());
  }, [pending]);

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((t) => selected.has(t.id));
  const toggleAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((t) => t.id)));

  const changeHead = async (g: FinanceAssignment, a: FinanceAssignment["assignees"][number]) => {
    const to = (newHead[a.id] || "").trim();
    if (!to) return toast.warning("Enter the new employee number.");
    if (
      !(await confirm(
        `Make ${to} the recovery head for "${g.name}" (${SCOPE_LABEL[a.wing_scope] || a.wing_scope})? ` +
          `All ${a.pending_count} pending request(s) of ${a.name || a.emp_id} for this group move to the new employee.`,
        { title: "Change Recovery Head", confirmLabel: "Change", tone: "warning" }
      ))
    )
      return;
    setBusy(true);
    const res = await changeFinanceAssignee({ assignee_id: a.id, new_emp_id: to });
    setBusy(false);
    if (res.success) {
      toast.success(`Recovery head changed — ${res.data?.moved ?? 0} pending request(s) moved.`);
      setNewHead((m) => ({ ...m, [a.id]: "" }));
      await load();
      onChanged?.();
    } else toast.error(res.message || "Failed to change the recovery head");
  };

  const transfer = async () => {
    if (!selected.size) return toast.warning("Select at least one pending request.");
    if (!transferTo.trim()) return toast.warning("Enter the employee number to transfer to.");
    if (
      !(await confirm(`Transfer ${selected.size} pending checklist(s) to ${transferTo.trim()}?`, {
        title: "Transfer Pending Requests",
        confirmLabel: "Transfer",
        tone: "warning",
      }))
    )
      return;
    setBusy(true);
    const res = await transferFinanceTasks({ task_ids: Array.from(selected), to_emp_id: transferTo.trim() });
    setBusy(false);
    if (res.success) {
      toast.success(`${res.data?.moved ?? 0} checklist(s) transferred.`);
      setTransferTo("");
      await load();
      onChanged?.();
    } else toast.error(res.message || "Failed to transfer");
  };

  if (loading) {
    return (
      <div className="text-center py-3 text-muted small">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading recovery heads…
      </div>
    );
  }

  return (
    <>
      {confirmDialog}
      <SectionCard
        index={2}
        title="Recovery Heads"
        subtitle="Change who checks a group of items. Pending requests of the previous employee move to the new one."
      >
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Group</th>
                <th>Applies to</th>
                <th>Recovery head</th>
                <th style={{ width: 90 }} className="text-center">Pending</th>
                <th style={{ width: 300 }}>Change to</th>
              </tr>
            </thead>
            <tbody>
              {assignments.flatMap((g) =>
                g.assignees.map((a, idx) => (
                  <tr key={a.id}>
                    {idx === 0 && (
                      <td rowSpan={g.assignees.length} className="fw-semibold">
                        {g.name}
                        {!!g.items?.length && (
                          <details className="fw-normal mt-1">
                            <summary className="small text-primary" style={{ cursor: "pointer" }}>
                              View items
                            </summary>
                            <ol className="small text-secondary mb-0 ps-3" start={g.items[0].sn}>
                              {g.items.map((it) => (
                                <li key={it.sn} value={it.sn}>{it.name}</li>
                              ))}
                            </ol>
                          </details>
                        )}
                      </td>
                    )}
                    <td>{SCOPE_LABEL[a.wing_scope] || a.wing_scope}</td>
                    <td>
                      {a.name || "—"} <span className="text-muted">({a.emp_id})</span>
                    </td>
                    <td className="text-center">{a.pending_count}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <input
                          className="form-control form-control-sm"
                          inputMode="numeric"
                          placeholder="New employee no."
                          value={newHead[a.id] || ""}
                          onChange={(e) => setNewHead((m) => ({ ...m, [a.id]: e.target.value }))}
                        />
                        <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => changeHead(g, a)}>
                          Change
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        index={3}
        title="Checklist Form"
        subtitle="The No-Due Certificate checklist — which item number is which, and who checks it."
      >
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 60 }}>S.N.</th>
                <th>Items to be checked</th>
                <th>Group</th>
                <th>Recovery head</th>
              </tr>
            </thead>
            <tbody>
              {assignments.flatMap((g) =>
                (g.items || []).map((it) => (
                  <tr key={`${g.group_id}-${it.sn}`}>
                    <td>{it.sn}</td>
                    <td>{it.name}</td>
                    <td className="text-muted small">{g.name}</td>
                    <td className="small">
                      {g.assignees
                        .map((a) =>
                          g.assignees.length > 1
                            ? `${a.name || a.emp_id} (${SCOPE_LABEL[a.wing_scope] || a.wing_scope})`
                            : a.name || a.emp_id
                        )
                        .join(" / ")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        index={4}
        title="Transfer Pending Requests"
        subtitle="Move one, several or all pending checklists from one employee to another."
      >
        {pending.length === 0 ? (
          <WarningBanner type="success" message="There are no pending checklists to transfer." />
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 260 }}
                value={filterEmp}
                onChange={(e) => {
                  setFilterEmp(e.target.value);
                  setSelected(new Set());
                }}
              >
                <option value="">All recovery heads</option>
                {assigneeOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name} ({id})
                  </option>
                ))}
              </select>
              <span className="text-muted small">{selected.size} selected</span>
              <div className="ms-auto d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  style={{ width: 190 }}
                  inputMode="numeric"
                  placeholder="Transfer to (employee no.)"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                />
                <button className="btn btn-sm btn-primary" disabled={busy || !selected.size} onClick={transfer}>
                  <i className="ti ti-arrows-exchange me-1" />
                  Transfer selected
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" className="form-check-input" checked={allVisibleSelected} onChange={toggleAll} />
                    </th>
                    <th>Request</th>
                    <th>Employee</th>
                    <th>Checklist</th>
                    <th>Assigned to</th>
                    <th style={{ width: 100 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <input type="checkbox" className="form-check-input" checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
                      </td>
                      <td>{t.request_id}</td>
                      <td>
                        {t.emp_name || "—"} <span className="text-muted">({t.emp_no || "—"})</span>
                      </td>
                      <td>{t.group_name}</td>
                      <td>
                        {t.assignee_name || "—"} <span className="text-muted">({t.assignee_emp_id})</span>
                      </td>
                      <td>{t.status === "LOCKED" ? "Locked" : "Pending"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>
    </>
  );
};

export default FinanceNodalAdmin;
